import { useEffect } from "react";
import { Panel } from "../ui/Panel.js";
import { formatDuration, useServerTick } from "../ui/Timer.js";
import { getLocale, useT } from "../i18n/index.js";
import { useAlliance } from "./state.js";
import type { DuelSide } from "./types.js";

const num = (n: number) => n.toLocaleString(getLocale() === "de" ? "de-DE" : "en-US");

/** Alliance › Duel: both alliances with live scores, the weekly theme, the viewer's contribution and milestone markers (82-alliance.md R80, D74). */
export function AllianceEvents() {
  const t = useT();
  const duel = useAlliance((s) => s.duel);
  const refresh = useAlliance((s) => s.refreshDuel);
  const now = useServerTick(1);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return (
    <div className="alliance-events" data-testid="alliance-events">
      <Panel title={t("alliance.duel.title")}>
        <p className="hint">{t("alliance.duel.hint")}</p>
        {!duel ? (
          <p className="hint" data-testid="duel-none">{t("alliance.duel.none")}</p>
        ) : (
          <>
            <div className="row" style={{ gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <span className="badge" data-testid="duel-theme">{t("alliance.duel.theme")}: {t(`alliance.duel.theme.${duel.theme}`)}</span>
              <span className={`badge mono ${duel.settled ? (duel.winnerId === duel.me.allianceId ? "ok" : "") : ""}`} data-testid="duel-status">
                {duel.settled ? (duel.winnerId === duel.me.allianceId ? t("alliance.duel.won") : duel.winnerId ? t("alliance.duel.lost") : t("alliance.duel.draw")) : t("alliance.duel.ends", { t: formatDuration(Math.max(0, duel.endsAt - now)) })}
              </span>
            </div>
            <div className="duel-card" data-testid="duel-card">
              <Side side={duel.me} id="me" />
              <div className="duel-vs">vs</div>
              {duel.opponent ? (
                <Side side={duel.opponent} id="opponent" />
              ) : (
                <div className="duel-side" data-testid="duel-target">
                  <span className="score">{num(duel.target)}</span>
                  <span className="hint">{t("alliance.duel.target", { n: num(duel.target) })}</span>
                </div>
              )}
            </div>
            <p className="hint" data-testid="duel-contribution">{t("alliance.duel.contribution", { n: num(duel.myContribution) })}</p>
            <h4 style={{ margin: "8px 0 0" }}>{t("alliance.duel.milestones")}</h4>
            <div className="duel-milestones">
              {duel.me.milestones.map((m) => (
                <span key={m.pct} className="duel-milestone" data-testid={`duel-milestone-${m.pct}`} data-reached={m.reached}>{t("alliance.duel.milestone", { pct: m.pct, n: num(m.points) })}</span>
              ))}
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

function Side({ side, id }: { side: DuelSide; id: "me" | "opponent" }) {
  return (
    <div className="duel-side" data-testid={`duel-side-${id}`}>
      <span className="alliance-tag small">{side.tag}</span>
      <strong className="ellipsis" style={{ maxWidth: "100%" }}>{side.name}</strong>
      <span className="score" data-testid={`duel-score-${id}`}>{num(side.score)}</span>
    </div>
  );
}
